/**
 * Base64 encode / decode — pure, UTF-8 safe.
 *
 * Uses the global `btoa`/`atob` (available on the server runtime and the
 * browser) over `TextEncoder`/`TextDecoder` bytes, so multibyte characters
 * (accents, emoji, CJK) round-trip correctly. Deterministic → safe to render
 * server-side.
 */

export interface Base64Result {
  ok: boolean;
  output?: string;
  error?: string;
}

function bytesToBinary(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return bin;
}

export function encodeBase64(text: string, urlSafe = false): Base64Result {
  try {
    const bytes = new TextEncoder().encode(text);
    let out = btoa(bytesToBinary(bytes));
    if (urlSafe) {
      out = out.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }
    return { ok: true, output: out };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Encode failed",
    };
  }
}

export function decodeBase64(b64: string, urlSafe = false): Base64Result {
  const trimmed = b64.trim();
  if (trimmed === "") return { ok: true, output: "" };
  try {
    let s = urlSafe ? trimmed.replace(/-/g, "+").replace(/_/g, "/") : trimmed;
    while (s.length % 4 !== 0) s += "=";
    const bin = atob(s);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return { ok: true, output: new TextDecoder().decode(bytes) };
  } catch {
    return { ok: false, error: "Invalid Base64 input" };
  }
}
